// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The six modes of the Bygning shell, as data (PLAN.md "Layout contract").
 * Each mode is a route; the shell reads the mode from the path and asks the
 * mode for a `ModeView` that fills some of the four tile slots. Gennemgang
 * is "get everything unblocked" (what blocks the report on the left, the type
 * on the right); Rapporter & indberetning has its tree of reports and
 * submissions on the left. Rediger (frontend-u0o) is the one editing mode —
 * for editors and owners only — with three sub-tabs as nested routes
 * (`/rediger/import`, `/rediger/scan`, `/rediger/geometri` — the order the
 * work goes: bring data in, repair the scan, then the building model). Pure: no
 * React, so the table is unit-testable in Node.
 */

import { GENNEMGANG_PATH, MILJOE_PATH, OVERBLIK_PATH, RAPPORT_PATH, REDIGER_PATH, RESSOURCER_PATH } from '../app/links';

export type ModeId = 'overblik' | 'ressourcer' | 'gennemgang' | 'miljoe' | 'rediger' | 'rapport';

/** One sub-tab of a mode: a nested route `<mode path>/<id>`. */
export interface ModeSubTab {
  id: string;
  /** The sub-tab's label; sentence-case Danish. */
  label: string;
  /** The key hint above the strip on this sub-tab. */
  hint: string;
}

/** The four tile slots floating over the Ground. */
export type SlotName = 'left' | 'right' | 'bottom' | 'centre';

/**
 * Where a mode tab's badge count comes from: `queue` = types awaiting review
 * (`SurveySummary.counts.queue`), `samples` = samples without an answer
 * (`SurveySummary.pending_samples`), `blocking` = types that keep the report
 * a kladde (`bygning/blocking.ts`, or `SurveyFractions.blocking_types`).
 */
export type BadgeSource = 'queue' | 'samples' | 'blocking' | null;

export interface ModeDef {
  id: ModeId;
  /** The tab label; sentence-case Danish. */
  label: string;
  /** The tab label on a narrow top bar (below 75rem), when the full one is long. */
  shortLabel?: string;
  /** Case-relative route. */
  path: string;
  /** The slots the mode fills (the layout contract's table). */
  slots: readonly SlotName[];
  badge: BadgeSource;
  /** The key hint above the strip, unless the mode's view overrides it. */
  hint: string;
  /** Whether the top bar shows the lens and Model/Tabel/Galleri switches. */
  lens: boolean;
  /** Whether the gear (Modelindstillinger) is offered; not on the document modes. */
  gear: boolean;
  /** Whether the Ground is drawn quiet (no type colours) behind the tiles. */
  quiet: boolean;
  /** Sub-tabs as nested routes (`/rediger/scan`); `path` alone picks the default one. */
  tabs?: readonly ModeSubTab[];
  /** Only editors and owners see the tab (a viewer's deep link lands read-only). */
  editOnly?: boolean;
}

/** In tab order (the v8 prototype's). */
export const MODES: readonly ModeDef[] = [
  {
    id: 'overblik',
    label: 'Overblik',
    path: OVERBLIK_PATH,
    slots: ['left', 'right', 'bottom'],
    badge: null,
    hint: 'Klik et rum for at fokusere · Esc tilbage',
    lens: false,
    gear: true,
    quiet: false,
  },
  {
    id: 'ressourcer',
    label: 'Ressourcer',
    path: RESSOURCER_PATH,
    slots: ['left', 'right', 'bottom', 'centre'],
    badge: null,
    hint: 'Træk for at rotere · J/K næste/forrige · G godkend · A afvis',
    lens: true,
    gear: true,
    quiet: false,
  },
  {
    id: 'miljoe',
    label: 'Miljø & prøver',
    path: MILJOE_PATH,
    slots: ['right', 'bottom'],
    badge: 'samples',
    hint: 'J/K næste/forrige prøve',
    lens: false,
    gear: true,
    quiet: false,
  },
  {
    id: 'gennemgang',
    label: 'Gennemgang',
    path: GENNEMGANG_PATH,
    slots: ['left', 'right', 'centre'],
    badge: 'blocking',
    hint: 'J/K næste/forrige · G godkend · A afvis',
    lens: false,
    gear: true,
    quiet: false,
  },
  {
    id: 'rediger',
    label: 'Rediger',
    path: REDIGER_PATH,
    slots: ['left', 'right', 'centre'],
    badge: null,
    hint: '',
    lens: false,
    gear: true,
    quiet: false,
    tabs: [
      { id: 'import', label: 'Import', hint: '' },
      { id: 'scan', label: 'Scan & pose', hint: '' },
      { id: 'geometri', label: 'Geometri', hint: '' },
    ],
    editOnly: true,
  },
  {
    id: 'rapport',
    label: 'Rapporter & indberetning',
    shortLabel: 'Rapp & Indb.',
    path: RAPPORT_PATH,
    slots: ['left', 'right', 'centre'],
    badge: null,
    hint: 'J/K næste/forrige side',
    lens: false,
    gear: false,
    quiet: true,
  },
];

const MODE_IDS = new Set<string>(MODES.map((m) => m.id));

export function isModeId(value: unknown): value is ModeId {
  return typeof value === 'string' && MODE_IDS.has(value);
}

export function modeById(id: ModeId): ModeDef {
  // MODES covers every ModeId; the fallback only satisfies the type checker.
  return MODES.find((m) => m.id === id) ?? MODES[0];
}

/** The route of a mode. */
export function modePath(id: ModeId): string {
  return modeById(id).path;
}

/** The modes whose tab a user sees: the edit-only ones (Rediger) only with edit rights. */
export function visibleModes(canEdit: boolean): readonly ModeDef[] {
  return canEdit ? MODES : MODES.filter((m) => !m.editOnly);
}

/**
 * The mode and sub-tab a case-relative path shows, or null for any other
 * path. A trailing slash is ignored; a nested path is a mode only when it
 * names one of the mode's sub-tabs (`/rediger/scan`). `tab` is null on the
 * mode's own path.
 */
export function modeTabByPath(pathname: string): { mode: ModeId; tab: string | null } | null {
  const trimmed = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  const path = trimmed === '' ? '/' : trimmed;
  for (const m of MODES) {
    if (m.path === path) return { mode: m.id, tab: null };
    if (!m.tabs || !path.startsWith(`${m.path}/`)) continue;
    const rest = path.slice(m.path.length + 1);
    const tab = m.tabs.find((t) => t.id === rest);
    if (tab) return { mode: m.id, tab: tab.id };
  }
  return null;
}

/** The mode a case-relative path shows, or null for any other path (see `modeTabByPath`). */
export function modeByPath(pathname: string): ModeDef | null {
  const hit = modeTabByPath(pathname);
  return hit ? modeById(hit.mode) : null;
}

/** Live figures a badge can show. */
export interface BadgeCounts {
  queue?: number;
  pendingSamples?: number;
  /** Types that block the report. */
  blocking?: number;
}

/** The count behind a mode's badge, or undefined for a mode without one. */
export function badgeCount(mode: ModeDef, counts: BadgeCounts): number | undefined {
  if (mode.badge === 'queue') return counts.queue;
  if (mode.badge === 'samples') return counts.pendingSamples;
  if (mode.badge === 'blocking') return counts.blocking;
  return undefined;
}
